import React, { useState } from 'react';
import ReasonForChangeDialog from '@components-clean/core/dialogs/reason-for-change-dialog';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import GridFe from '@components-clean/core/grid-fe';
import { EditReviewDto } from '@interfaces-clean/reviews.interface';
import { ReviewSubjectDto } from '@interfaces-clean/subject-monitoring/subject-review';
import { useBoolean, useTranslation } from '@hooks/index';
import { doFormat } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { SubjectReviewInstanceApi } from '@apis/bff-api/subject-monitoring/subject-review-instance';

type Props = {
    canEdit: boolean;
    studyRef: string;
    review: EditReviewDto;
    reviewSubjects: ReviewSubjectDto[];
    reviewSubject?: boolean;
    sessionId?: string;
};

const SubjectsOnReviewGrid = ({ canEdit, studyRef, review, reviewSubjects, reviewSubject, sessionId }: Props) => {
    const { t } = useTranslation('sm', { keyPrefix: 'monitoringToolkit.activeReviewTab.tabs.reviewPlan.subjects' });
    const { t: tCommon } = useTranslation('common');

    const { StandardCell } = createGridCustomCells();

    const setReviewedMutation = SubjectReviewInstanceApi.useSetSubjectReviewInstanceReviewedMutation(studyRef);
    const removeFromReviewMutation = SubjectReviewInstanceApi.useRemoveFromReview(studyRef);
    const isMutating = setReviewedMutation.isMutating || removeFromReviewMutation.isMutating;

    const showRemoveDialog = useBoolean(false);
    const [pendingRemoveSubject, setPendingRemoveSubject] = useState<ReviewSubjectDto | null>(null);

    const handleSetReviewed = (dataItem: ReviewSubjectDto, setToReviewed: boolean) => {
        if (!canEdit || isMutating) {
            return;
        }

        setReviewedMutation
            .trigger({
                reviewRecordId: review.recordId,
                datasetId: review.datasetId,
                subjectId: dataItem.subjectId,
                setToReviewed,
                sessionId,
            })
            .then(() => ShowSuccess(tCommon('actionsSuccessfulToast')))
            .catch(() => ShowError(tCommon('errorMessage')));
    };

    const handleRemoveFromReviewClick = (dataItem: ReviewSubjectDto) => {
        if (!canEdit || isMutating) {
            return;
        }

        setPendingRemoveSubject(dataItem);
        showRemoveDialog.setTrue();
    };

    const handleRemoveFromReview = async (reason: string) => {
        if (!canEdit || pendingRemoveSubject == null) {
            return;
        }

        try {
            const result = await removeFromReviewMutation.trigger({
                reviewRecordId: review.recordId!,
                datasetId: review.datasetId,
                subjectIds: [pendingRemoveSubject.subjectId!],
                sessionId,
                reason,
                removeAll: false,
            });
            result ? ShowSuccess(tCommon('actionsSuccessfulToast')) : ShowError(tCommon('errorMessage'));
        } catch {
            ShowError(tCommon('errorMessage'));
        }
        showRemoveDialog.setFalse();
        setPendingRemoveSubject(null);
    };

    const gridData = reviewSubjects.map((s) => ({
        ...s,
        reviewedDisplay: s.reviewed ? 'Yes' : 'No',
        addedOnDisplay: s.addedOn ? doFormat(new Date(s.addedOn)) : null,
        lastReviewedOnDisplay: s.lastReviewedOn ? doFormat(new Date(s.lastReviewedOn)) : null,
    }));

    // For some reason, the column headers don't update when the `columns` prop changes...
    // So anything used in the column header needs to be part of a key to force a remount when they change...
    const key = review.type;

    return (
        <>
            <GridFe
                key={key}
                testDataId="subjects-on-review-grid"
                title={t('title')}
                maxHeight="100%"
                showAddIcon={false}
                gridDownloadDisabled
                rowMenuActions={
                    canEdit
                        ? [
                              {
                                  id: 1,
                                  label: t('actions.markAsReviewed'),
                                  icon: ['far', 'circle-check'],
                                  rowDisabled: (dataItem) => {
                                      const subject = dataItem as ReviewSubjectDto;
                                      return !reviewSubject || subject.reviewed === true;
                                  },
                                  onClick: (dataItem) => handleSetReviewed(dataItem as ReviewSubjectDto, true),
                              },
                              {
                                  id: 2,
                                  label: t('actions.markAsUnreviewed'),
                                  icon: ['far', 'circle-xmark'],
                                  rowDisabled: (dataItem) => {
                                      const subject = dataItem as ReviewSubjectDto;
                                      return !reviewSubject || subject.reviewed !== true;
                                  },
                                  onClick: (dataItem) => handleSetReviewed(dataItem as ReviewSubjectDto, false),
                              },
                              {
                                  id: 3,
                                  label: t('actions.removeFromReview'),
                                  icon: ['far', 'trash-can'],
                                  colorProp: 'Red',
                                  onClick: (dataItem) => handleRemoveFromReviewClick(dataItem as ReviewSubjectDto),
                              },
                          ]
                        : undefined
                }
                columns={[
                    {
                        id: 'siteName',
                        field: 'siteName',
                        title: t('columns.site'),
                        minWidth: 140,
                        cells: { data: StandardCell },
                    },
                    {
                        id: 'subjectNumber',
                        field: 'subjectNumber',
                        title: t('columns.subject'),
                        minWidth: 140,
                        cells: { data: StandardCell },
                    },
                    {
                        id: 'reviewedDisplay',
                        field: 'reviewedDisplay',
                        title: t('columns.reviewed'),
                        minWidth: 120,
                        cells: { data: StandardCell },
                        lovData: ['Yes', 'No'],
                    },
                    {
                        id: 'lastReviewedByName',
                        field: 'lastReviewedByName',
                        title: t('columns.lastReviewTypeReviewedBy', { reviewType: review.type }),
                        minWidth: 160,
                        cells: { data: StandardCell },
                    },
                    {
                        id: 'lastReviewedOnDisplay',
                        field: 'lastReviewedOnDisplay',
                        title: t('columns.lastReviewTypeReviewedOn', { reviewType: review.type }),
                        minWidth: 160,
                        cells: { data: StandardCell },
                    },
                    {
                        id: 'addedByName',
                        field: 'addedByName',
                        title: t('columns.addedBy'),
                        minWidth: 160,
                        cells: { data: StandardCell },
                    },
                    {
                        id: 'addedOnDisplay',
                        field: 'addedOnDisplay',
                        title: t('columns.addedOn'),
                        minWidth: 160,
                        cells: { data: StandardCell },
                    },
                ]}
                data={gridData}
                scrollable="virtual"
            />

            {showRemoveDialog.value && (
                <ReasonForChangeDialog
                    maxLength={3000}
                    onSubmit={handleRemoveFromReview}
                    onCancel={() => {
                        showRemoveDialog.setFalse();
                        setPendingRemoveSubject(null);
                    }}
                />
            )}
        </>
    );
};

export default SubjectsOnReviewGrid;
